import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RADIUS } from "@/theme/tokens";
import { JumpingDots } from "@/ui/Animations/JumpingDots";
import { type SystemChipItem, SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EyebrowLabel } from "@/ui/molecules/EyebrowLabel";
import { InlineHint } from "@/ui/molecules/InlineHint";
import type { GapsRemovalReasons, GapsRemovalState } from "./useGapsRemoval";

type Props = {
	reasons: GapsRemovalReasons;
	selectedReason: GapsRemovalState["selectedReason"];
	/** The wrapped hook's shake, played on the chips when confirm is pressed without a reason. */
	shakeControls: GapsRemovalState["reasonShakeControls"];
	onPick: (reason: string) => void;
	testIdPrefix: string;
};

/** The shake target's corners, so the shaken box matches the chips' own rounding rather than a sharp rectangle. */
const reasonShakeStyle = { borderRadius: RADIUS.CARD } as const;

/**
 * The removal's reason: the department's call-in reasons as touch chips, one pick. A read that failed, or a department
 * with none, says so rather than showing an empty picker that would read as "this department has no reasons".
 */
export const GapsRemovalReasonPicker: FC<Props> = ({ reasons, selectedReason, shakeControls, onPick, testIdPrefix }) => {
	const { t } = useTranslation();
	const reasonChips = useMemo<SystemChipItem<string>[]>(
		() => reasons.options.map((option) => ({ value: option.value, label: option.label })),
		[reasons.options],
	);
	const selectedChips = useMemo(() => reasonChips.filter((chip) => chip.value === selectedReason), [reasonChips, selectedReason]);

	return (
		<Box data-testid={`${testIdPrefix}-reasons`}>
			<EyebrowLabel sx={{ mb: 0.75 }}>{t("gaps.callIn.removal.reasonLabel")}</EyebrowLabel>
			{reasons.isLoading ? (
				<JumpingDots sx={{ py: 1 }} />
			) : (
				// The hook's own shake target: it plays when confirm is pressed without a reason, so it must be mounted.
				<motion.div animate={shakeControls} style={reasonShakeStyle}>
					<SystemChips<string>
						items={reasonChips}
						selectedItems={selectedChips}
						onChange={(item, isSelected) => {
							if (isSelected) onPick(item.value);
						}}
						touchTarget
						dataTestId={`${testIdPrefix}-reason-chips`}
						getItemDataTestId={(item) => `${testIdPrefix}-reason-${item.value}`}
						rootSx={{ justifyContent: "flex-start" }}
					/>
				</motion.div>
			)}
			<InlineHint open={reasons.isError} tone="warning" dataTestId={`${testIdPrefix}-reasons-error`}>
				{t("error.general")}
			</InlineHint>
			<InlineHint open={reasons.isSuccess && reasons.options.length === 0} tone="warning" dataTestId={`${testIdPrefix}-no-reasons`}>
				{t("gaps.callIn.removal.noReasons")}
			</InlineHint>
		</Box>
	);
};
